import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Android as AndroidIcon,
  EditNote as EditNoteIcon,
  ReportProblem as ReportProblemIcon,
  RestartAlt as RestartAltIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import siteStatusService, {
  StoreChannel,
  StoreChannelPolicyKey,
  StoreChannelPolicyState,
} from '../services/siteStatus.service';

/**
 * «نسخهٔ استورها» — روشن/خاموش کردن تک‌تک قابلیت‌های حساس، فقط برای بیلدِ اپِ
 * یک استور مشخص (کافه‌بازار، مایکت، گوگل‌پلی).
 *
 * ⚠️ این صفحه عمداً از «قابلیت‌های سایت (Feature Flags)» جداست: آن یکی سراسری
 * است و وب را هم خاموش می‌کند؛ این یکی فقط به اپِ همان کانالِ پخش می‌گوید کدام
 * بخش را نشان ندهد. وب هیچ‌وقت تحت تأثیر این صفحه نیست.
 */

const CHANNEL_HINT: Record<StoreChannel, string> = {
  direct:
    'نسخهٔ دانلود مستقیم از سایت خودمان. خارج از قوانین استورهاست و همه‌چیز در آن مجاز است — معمولاً نیازی به تغییر ندارد.',
  bazaar:
    'نسخه‌ای که روی کافه‌بازار منتشر می‌شود. موارد خاموش در این ستون برای مسئول بررسی کافه‌بازار اصلاً نمایش داده نمی‌شوند.',
  myket: 'نسخهٔ مایکت — قوانینش تقریباً مشابه کافه‌بازار است.',
  playstore: 'نسخهٔ گوگل‌پلی.',
};

const StorePolicyManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [channel, setChannel] = useState<StoreChannel>('bazaar');
  const [channels, setChannels] = useState<Array<{ key: StoreChannel; label: string }>>([]);
  const [policies, setPolicies] = useState<StoreChannelPolicyState[]>([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [noteTarget, setNoteTarget] = useState<StoreChannelPolicyState | null>(null);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const load = useCallback(
    async (ch: StoreChannel) => {
      setLoading(true);
      try {
        const data = await siteStatusService.listStorePolicies(ch);
        setPolicies(data.policies || []);
        if (data.channels?.length) setChannels(data.channels);
      } catch {
        enqueueSnackbar('خطا در دریافت تنظیمات نسخهٔ استور', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    load(channel);
  }, [channel, load]);

  const handleToggle = async (policy: StoreChannelPolicyState) => {
    const next = !policy.enabled;
    setPolicies((prev) =>
      prev.map((p) => (p.key === policy.key ? { ...p, enabled: next, isOverridden: true } : p))
    );
    try {
      await siteStatusService.updateStorePolicy(channel, policy.key as StoreChannelPolicyKey, {
        enabled: next,
      });
      enqueueSnackbar(
        next
          ? `«${policy.nameFa}» در نسخهٔ ${channelLabel(channel)} نمایش داده می‌شود`
          : `«${policy.nameFa}» در نسخهٔ ${channelLabel(channel)} پنهان شد`,
        { variant: next ? 'warning' : 'success' }
      );
    } catch {
      setPolicies((prev) =>
        prev.map((p) => (p.key === policy.key ? { ...p, enabled: policy.enabled } : p))
      );
      enqueueSnackbar('خطا در تغییر وضعیت', { variant: 'error' });
    }
  };

  const channelLabel = (ch: StoreChannel) =>
    channels.find((c) => c.key === ch)?.label || ch;

  const handleReset = async () => {
    setResetting(true);
    try {
      const next = await siteStatusService.resetStorePolicies(channel);
      setPolicies(next);
      enqueueSnackbar('به مقادیر پیشنهادی بازگشت', { variant: 'info' });
    } catch {
      enqueueSnackbar('خطا در بازنشانی', { variant: 'error' });
    } finally {
      setResetting(false);
    }
  };

  const saveNote = async () => {
    if (!noteTarget) return;
    setSavingNote(true);
    try {
      await siteStatusService.updateStorePolicy(
        channel,
        noteTarget.key as StoreChannelPolicyKey,
        { enabled: noteTarget.enabled, note: noteText }
      );
      setPolicies((prev) =>
        prev.map((p) => (p.key === noteTarget.key ? { ...p, note: noteText } : p))
      );
      enqueueSnackbar('یادداشت ذخیره شد', { variant: 'success' });
      setNoteTarget(null);
    } catch {
      enqueueSnackbar('خطا در ذخیرهٔ یادداشت', { variant: 'error' });
    } finally {
      setSavingNote(false);
    }
  };

  const hiddenCount = policies.filter((p) => !p.enabled).length;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            نسخهٔ استورها (کافه‌بازار / مایکت / گوگل‌پلی)
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            هر قابلیتی که تیکش را بردارید، در بیلدِ اپِ آن استور اصلاً نمایش داده نمی‌شود — نه
            برای مسئول بررسی استور و نه برای کاربران آن نسخه. وب و نسخهٔ دانلود مستقیم بدون
            تغییر می‌مانند.
          </Typography>
        </Box>
        <Button
          onClick={handleReset}
          startIcon={<RestartAltIcon />}
          variant="outlined"
          size="small"
          disabled={resetting || loading}
        >
          بازگشت به مقادیر پیشنهادی
        </Button>
      </Stack>

      <Alert severity="warning" icon={<ShieldIcon />} sx={{ mb: 2 }}>
        <AlertTitle>این ابزار برای انطباق است، نه دور زدن بررسی</AlertTitle>
        اگر اپ را با یک قابلیتِ خاموش به استور بدهید و بعد از تأیید روشنش کنید، خودِ همین کار
        تخلف محسوب می‌شود و می‌تواند به حذف اپ منجر شود. تغییرات تا حداکثر ۳۰ ثانیه در اپ‌های
        باز اعمال می‌شود.
      </Alert>

      <Tabs
        value={channel}
        onChange={(_, v) => setChannel(v)}
        sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}
      >
        {(channels.length
          ? channels
          : [{ key: 'bazaar' as StoreChannel, label: 'کافه‌بازار' }]
        ).map((c) => (
          <Tab key={c.key} value={c.key} label={c.label} icon={<AndroidIcon />} iconPosition="start" />
        ))}
      </Tabs>

      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary" flex={1}>
          {CHANNEL_HINT[channel]}
        </Typography>
        <Chip
          size="small"
          color={hiddenCount ? 'success' : 'default'}
          label={`${hiddenCount} مورد پنهان`}
        />
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {policies.map((policy) => (
            <Card
              key={policy.key}
              variant="outlined"
              sx={{
                borderColor: policy.enabled ? 'warning.light' : 'success.light',
                borderWidth: 2,
              }}
            >
              <CardContent sx={{ pb: 1.5 }}>
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <Box flex={1}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="h6" fontWeight={700}>
                        {policy.nameFa}
                      </Typography>
                      <Chip
                        size="small"
                        label={policy.enabled ? 'نمایش داده می‌شود' : 'پنهان'}
                        color={policy.enabled ? 'warning' : 'success'}
                      />
                      {policy.severity === 'blocker' && (
                        <Tooltip title="طبق قوانین استور، نمایش این مورد می‌تواند باعث رد شدن اپ شود">
                          <Chip
                            size="small"
                            icon={<ReportProblemIcon />}
                            label="ریسک رد شدن"
                            color="error"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                      {policy.isOverridden && (
                        <Chip size="small" label="دستی تنظیم شده" variant="outlined" />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                      <code>{policy.key}</code>
                    </Typography>
                  </Box>
                  <Tooltip
                    title={policy.enabled ? 'برای پنهان‌کردن، تیک را بردارید' : 'برای نمایش، تیک بزنید'}
                  >
                    <Switch
                      checked={policy.enabled}
                      onChange={() => handleToggle(policy)}
                      color="warning"
                    />
                  </Tooltip>
                </Stack>

                <Typography variant="body2" color="text.secondary" mt={1}>
                  {policy.descriptionFa}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                <Alert severity="info" sx={{ fontSize: '0.82rem', py: 0.25 }}>
                  <strong>چرا حساس است:</strong> {policy.reasonFa}
                </Alert>

                {policy.note && (
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    یادداشت: {policy.note}
                  </Typography>
                )}

                <Button
                  size="small"
                  startIcon={<EditNoteIcon />}
                  onClick={() => {
                    setNoteTarget(policy);
                    setNoteText(policy.note || '');
                  }}
                  sx={{ mt: 0.5 }}
                >
                  یادداشت داخلی
                </Button>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={!!noteTarget} onClose={() => setNoteTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>یادداشت برای «{noteTarget?.nameFa}»</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            فقط برای ادمین‌ها دیده می‌شود — مثلاً دلیل تصمیم یا ارجاع به ایمیل بازبینِ استور.
          </Typography>
          <TextField
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            fullWidth
            multiline
            rows={3}
            inputProps={{ maxLength: 500 }}
            placeholder="مثلاً: طبق ایمیل بازبین کافه‌بازار در ۱۴۰۵/۰۵/۲۹ خاموش شد."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteTarget(null)}>انصراف</Button>
          <Button onClick={saveNote} variant="contained" disabled={savingNote}>
            {savingNote ? 'در حال ذخیره...' : 'ذخیره'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StorePolicyManagement;
